import { motion, type Transition } from 'framer-motion';
import type { CSSProperties, FunctionComponent } from 'react';
import { memo, useEffect, useState } from 'react';

import { useChatComposerActive, useChatComposerCallbacks } from '../chat-composer-contexts';
import {
    CHAT_BAR_HEIGHT_PX,
    COLLAPSED_COMPOSER_BOTTOM_OFFSET_PX,
    COMPOSER_INSET_LEFT_VARIABLE,
    COMPOSER_LAYOUT_ID,
    COMPOSER_TRANSITION,
} from '../constants';
import { CollapsedComposer } from './CollapsedComposer';
import { PILL_COMPOSER_WIDTH_CSS } from './PillChatComposer';

const PILL_LAYOUT_BOX_HEIGHT_PX = CHAT_BAR_HEIGHT_PX - 2;

const PILL_ENTRY_OFFSET_Y_PX = CHAT_BAR_HEIGHT_PX + COLLAPSED_COMPOSER_BOTTOM_OFFSET_PX;
const PILL_ENTRY_INITIAL = { opacity: 0, y: PILL_ENTRY_OFFSET_Y_PX } as const;
const PILL_ENTRY_ANIMATE = { opacity: 1, y: 0 } as const;

const FOCUS_GRADIENT_HEIGHT_PX = 150 + COLLAPSED_COMPOSER_BOTTOM_OFFSET_PX;
const FOCUS_GRADIENT_COLOR = 'var(--semantic-surface-canvas)';
const FOCUS_GRADIENT_BACKGROUND = `linear-gradient(to top, ${FOCUS_GRADIENT_COLOR} 0%, ${FOCUS_GRADIENT_COLOR} 35%, color-mix(in srgb, ${FOCUS_GRADIENT_COLOR} 93%, transparent) 65%, transparent 100%)`;
const FOCUS_GRADIENT_MASK = 'linear-gradient(to top, black 0%, black 50%, transparent 90%)';
const FOCUS_GRADIENT_STYLE: CSSProperties = {
    height: FOCUS_GRADIENT_HEIGHT_PX,
    background: FOCUS_GRADIENT_BACKGROUND,
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    maskImage: FOCUS_GRADIENT_MASK,
    WebkitMaskImage: FOCUS_GRADIENT_MASK,
};
const FOCUS_GRADIENT_TRANSITION = { duration: 0.32, ease: [0.25, 1, 0.5, 1] as const };
const COMPOSER_GRADIENT_REVEAL_HEIGHT_PX = 50;
const COMPOSER_BOTTOM_STYLE = `calc(env(safe-area-inset-bottom, 0px) + ${COLLAPSED_COMPOSER_BOTTOM_OFFSET_PX}px)`;

const COMPOSER_POSITION_STYLE: CSSProperties = {
    bottom: COMPOSER_BOTTOM_STYLE,
    left: 0,
    minHeight: CHAT_BAR_HEIGHT_PX,
    right: 0,
};

export const FloatingCollapsedComposer: FunctionComponent<{
    readonly horizontalOffsetPx?: number;
    readonly horizontalOffsetTransition?: Transition;
}> = memo(({ horizontalOffsetPx = 0, horizontalOffsetTransition }) => {
    const { isExpanded } = useChatComposerActive();
    const { hasComposerAppeared, isComposerReady, markComposerAppeared, setCollapsedComposerOpen } =
        useChatComposerCallbacks();
    const [isComposerOpen, setIsComposerOpen] = useState(false);
    const [composerNode, setComposerNode] = useState<HTMLDivElement | null>(null);
    const [isComposerTall, setIsComposerTall] = useState(false);

    useEffect(() => {
        if (composerNode == null) {
            return;
        }
        const observer = new ResizeObserver(([entry]) => {
            if (entry == null) return;
            const tall = entry.contentRect.height > COMPOSER_GRADIENT_REVEAL_HEIGHT_PX;
            setIsComposerTall((previous) => (previous === tall ? previous : tall));
        });
        observer.observe(composerNode);
        return () => observer.disconnect();
    }, [composerNode]);

    const show = isComposerReady && !isExpanded;
    const overlayShown = show && isComposerOpen && isComposerTall;

    useEffect(() => {
        if (!show) {
            setIsComposerOpen(false);
        }
    }, [show]);

    useEffect(() => {
        setCollapsedComposerOpen(show && isComposerOpen);
    }, [isComposerOpen, setCollapsedComposerOpen, show]);

    useEffect(() => () => setCollapsedComposerOpen(false), [setCollapsedComposerOpen]);

    return (
        <motion.div
            className="pointer-events-none fixed right-0 bottom-0 z-[900] overflow-visible"
            style={{ left: `var(${COMPOSER_INSET_LEFT_VARIABLE}, 0px)` }}
            animate={{ x: horizontalOffsetPx }}
            initial={false}
            transition={horizontalOffsetTransition}
        >
            <motion.div
                aria-hidden={true}
                className="absolute inset-x-0 bottom-0"
                initial={false}
                animate={{ opacity: overlayShown ? 1 : 0 }}
                transition={FOCUS_GRADIENT_TRANSITION}
                style={{ ...FOCUS_GRADIENT_STYLE, pointerEvents: 'none' }}
            />
            <div className="absolute flex items-end justify-center" style={COMPOSER_POSITION_STYLE}>
                {show && (
                    <motion.div
                        layoutId={COMPOSER_LAYOUT_ID}
                        layout="position"
                        className="pointer-events-auto relative"
                        style={{ width: PILL_COMPOSER_WIDTH_CSS, height: PILL_LAYOUT_BOX_HEIGHT_PX }}
                        initial={hasComposerAppeared ? false : PILL_ENTRY_INITIAL}
                        animate={hasComposerAppeared ? undefined : PILL_ENTRY_ANIMATE}
                        transition={COMPOSER_TRANSITION}
                        onAnimationComplete={hasComposerAppeared ? undefined : markComposerAppeared}
                    >
                        <div ref={setComposerNode} className="absolute inset-x-0 bottom-0">
                            <CollapsedComposer onOpenChange={setIsComposerOpen} />
                        </div>
                    </motion.div>
                )}
            </div>
        </motion.div>
    );
});
